// components/Benchmark/intelUI.jsx
// Shared presentation primitives for the market and product intelligence views.
import React from "react";
import { Text, Tooltip } from "@fluentui/react-components";
import {
  Warning24Regular, CheckmarkCircle24Regular, Info24Regular,
  DataBarVertical24Regular, Search24Regular, ChevronDown12Regular,
} from "@fluentui/react-icons";

// ── Palette ───────────────────────────────────────────────────────────────────
export const GOLD = "#A66400";
export const GOLD_LT = "#C87800";
export const NAVY = "#001A3D";
export const GOOD = "#107C10";
export const WARN = "#C4762A";
export const BAD = "#C4262E";
export const INK = "#333";
export const MUTED = "#888";

export const SERIES_COLORS = [
  "#0066FF", "#006E6E", "#7B2D8B", "#C4262E", "#D1521A", "#637074",
  "#005A9E", "#006840", "#B87000", "#8B3A3A", "#2D6A8B", "#4A7B4A",
];

export const CONF_STYLE = {
  High: { bg: "#EAF6EA", color: GOOD },
  Good: { bg: "#EBF3FF", color: "#0052A3" },
  Fair: { bg: "#FFF4EC", color: WARN },
  Low:  { bg: "#FDE7E9", color: BAD },
  None: { bg: "#F3F2F1", color: MUTED },
};

export const SEVERITY = {
  critical: { bg: "#FDE7E9", border: "#F5C2C7", color: BAD,  Icon: Warning24Regular },
  warn:     { bg: "#FFF4EC", border: "#FFD9B0", color: WARN, Icon: Warning24Regular },
  good:     { bg: "#EAF6EA", border: "#BFE3BF", color: GOOD, Icon: CheckmarkCircle24Regular },
  info:     { bg: "#F0F4FA", border: "#D6E2F0", color: "#0052A3", Icon: Info24Regular },
};

// ── Table cell styles ─────────────────────────────────────────────────────────
export const th = (align = "left", extra = {}) => ({
  padding: "10px 12px", color: "#C8D8FF", fontSize: 10, fontWeight: 700,
  letterSpacing: "0.5px", textAlign: align, whiteSpace: "nowrap", ...extra,
});

export const td = (align = "left", extra = {}) => ({
  padding: "9px 12px", textAlign: align, fontSize: 12, color: INK,
  fontVariantNumeric: "tabular-nums", ...extra,
});

// ── Building blocks ───────────────────────────────────────────────────────────

export function ConfidenceChip({ confidence, showNote }) {
  if (!confidence) return null;
  const s = CONF_STYLE[confidence.level] || CONF_STYLE.None;
  return (
    <Tooltip content={confidence.note} relationship="label">
      <span style={{ background: s.bg, color: s.color, padding: "2px 9px", borderRadius: 10, fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap" }}>
        {confidence.level} confidence{showNote ? ` · ${confidence.note}` : ""}
      </span>
    </Tooltip>
  );
}

export function Panel({ title, sub, right, children, pad = "20px 24px" }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #E4E8EF", borderRadius: 12, padding: pad, marginBottom: 16 }}>
      {(title || right) && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: sub ? 4 : 14, flexWrap: "wrap" }}>
          <div>
            {title && <Text weight="semibold" style={{ color: NAVY, fontSize: 14, display: "block" }}>{title}</Text>}
            {sub && <Text style={{ fontSize: 12, color: MUTED, display: "block" }}>{sub}</Text>}
          </div>
          {right}
        </div>
      )}
      <div style={{ marginTop: sub ? 14 : 0 }}>{children}</div>
    </div>
  );
}

export function KPI({ label, value, sub, color = GOLD_LT, Icon = DataBarVertical24Regular }) {
  return (
    <div style={{ flex: 1, minWidth: 150, background: "#fff", border: `1.5px solid ${color}28`, borderRadius: 14, padding: "16px 18px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, gap: 8 }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon style={{ color, fontSize: 17 }} />
        </div>
        <span style={{ fontSize: 20, fontWeight: 800, color: NAVY, lineHeight: 1.15, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{value}</span>
      </div>
      <span style={{ fontSize: 10, fontWeight: 700, color: MUTED, letterSpacing: "0.6px", display: "block" }}>{label.toUpperCase()}</span>
      {sub && <span style={{ fontSize: 11, color, display: "block", marginTop: 2 }}>{sub}</span>}
    </div>
  );
}

export function Empty({ children }) {
  return (
    <div style={{ padding: "44px 20px", textAlign: "center", color: "#AAA", fontSize: 13 }}>{children}</div>
  );
}

export function InsightList({ insights }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {insights.map((ins, i) => {
        const s = SEVERITY[ins.severity] || SEVERITY.info;
        return (
          <div key={i} style={{ display: "flex", gap: 12, background: s.bg, border: `1px solid ${s.border}`, borderRadius: 10, padding: "13px 16px" }}>
            <s.Icon style={{ color: s.color, fontSize: 18, flexShrink: 0, marginTop: 1 }} />
            <div>
              <Text style={{ fontSize: 13, fontWeight: 700, color: NAVY, display: "block", marginBottom: 3 }}>{ins.title}</Text>
              <Text style={{ fontSize: 12.5, color: "#444", lineHeight: 1.65 }}>{ins.body}</Text>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Multi-series line chart (SVG) ─────────────────────────────────────────────

export function LineChart({ series, yLabel, height = 260, yFormat, baseline }) {
  const all = series.flatMap((s) => s.points);
  if (all.length < 1) return <Empty>Not enough data to plot.</Empty>;

  const W = 720, H = height;
  const pad = { t: 14, r: 16, b: 34, l: 66 };
  const iw = W - pad.l - pad.r;
  const ih = H - pad.t - pad.b;

  const xs = all.map((p) => p.x);
  const ys = all.map((p) => p.y);
  const xMin = Math.min(...xs), xMax = Math.max(...xs);
  let yMin = Math.min(...ys), yMax = Math.max(...ys);
  if (baseline !== undefined) { yMin = Math.min(yMin, baseline); yMax = Math.max(yMax, baseline); }
  const padY = (yMax - yMin) * 0.12 || Math.abs(yMax * 0.12) || 1;
  yMin -= padY; yMax += padY;

  const sx = (x) => pad.l + (xMax === xMin ? iw / 2 : ((x - xMin) / (xMax - xMin)) * iw);
  const sy = (y) => pad.t + ih - (yMax === yMin ? ih / 2 : ((y - yMin) / (yMax - yMin)) * ih);
  const fy = yFormat || ((v) => v.toFixed(0));

  const ticks = 4;
  const yTicks = Array.from({ length: ticks + 1 }, (_, i) => yMin + ((yMax - yMin) * i) / ticks);
  const xTickVals = [...new Set(xs)].sort((a, b) => a - b);
  const xTicks = xTickVals.length > 8
    ? xTickVals.filter((_, i) => i % Math.ceil(xTickVals.length / 8) === 0)
    : xTickVals;

  return (
    <div style={{ overflowX: "auto" }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", minWidth: 480, display: "block" }} role="img" aria-label={yLabel || "chart"}>
        {yTicks.map((v, i) => (
          <g key={i}>
            <line x1={pad.l} y1={sy(v)} x2={W - pad.r} y2={sy(v)} stroke="#EEF0F4" strokeWidth="1" />
            <text x={pad.l - 8} y={sy(v) + 4} textAnchor="end" fontSize="10" fill="#999" fontFamily="Segoe UI,system-ui,sans-serif">{fy(v)}</text>
          </g>
        ))}
        {baseline !== undefined && (
          <line x1={pad.l} y1={sy(baseline)} x2={W - pad.r} y2={sy(baseline)} stroke="#B0B8C4" strokeWidth="1.5" strokeDasharray="5 4" />
        )}
        {xTicks.map((x) => (
          <text key={x} x={sx(x)} y={H - 12} textAnchor="middle" fontSize="10.5" fill="#777" fontFamily="Segoe UI,system-ui,sans-serif">
            {series[0]?.xLabel ? series[0].xLabel(x) : x}
          </text>
        ))}
        <line x1={pad.l} y1={pad.t} x2={pad.l} y2={pad.t + ih} stroke="#D0D7E5" strokeWidth="1" />

        {series.map((s, si) => {
          const pts = [...s.points].sort((a, b) => a.x - b.x);
          if (!pts.length) return null;
          const d = pts.map((p, i) => `${i === 0 ? "M" : "L"}${sx(p.x).toFixed(1)},${sy(p.y).toFixed(1)}`).join(" ");
          const color = s.color || SERIES_COLORS[si % SERIES_COLORS.length];
          return (
            <g key={s.label}>
              {pts.length > 1 && <path d={d} fill="none" stroke={color} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />}
              {pts.map((p, i) => (
                <circle key={i} cx={sx(p.x)} cy={sy(p.y)} r={i === pts.length - 1 ? 4.5 : 3}
                  fill={i === pts.length - 1 ? color : "#fff"} stroke={color} strokeWidth="2">
                  <title>{`${s.label}\n${p.tip || `${p.x}: ${fy(p.y)}`}`}</title>
                </circle>
              ))}
            </g>
          );
        })}
      </svg>
      {series.length > 1 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 10, paddingLeft: 4 }}>
          {series.map((s, si) => (
            <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 12, height: 3, borderRadius: 2, background: s.color || SERIES_COLORS[si % SERIES_COLORS.length] }} />
              <span style={{ fontSize: 11, color: "#555" }}>{s.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Change badge ──────────────────────────────────────────────────────────────

export function ChangeBadge({ value, suffix = "", neutralBelow = 0.005 }) {
  if (value === null || value === undefined || !isFinite(value)) {
    return <span style={{ color: "#CCC" }}>—</span>;
  }
  const up = value > neutralBelow;
  const down = value < -neutralBelow;
  const color = down ? GOOD : up ? BAD : "#666";
  const bg = down ? "#EAF6EA" : up ? "#FDE7E9" : "#F3F2F1";
  return (
    <span style={{ background: bg, color, padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
      {value > 0 ? "+" : ""}{(value * 100).toFixed(1)}%{suffix}
    </span>
  );
}

// ── Searchable select ─────────────────────────────────────────────────────────
//
// A type-to-filter picker for lists that outgrow a plain dropdown. Each option
// can carry a count and a sub-line, so a buyer choosing a commodity can see how
// much history sits behind it and what unit it is priced on before selecting.

export function SearchableSelect({
  value, onChange, options, placeholder = "Search…", disabled,
  emptyText = "Nothing matches that search.",
  clearLabel, accent = GOLD,
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [active, setActive] = React.useState(0);
  const rootRef = React.useRef(null);
  const listRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const listId = React.useId();

  const selected = options.find((o) => o.value === value) || null;

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      o.label.toLowerCase().includes(q) ||
      (o.meta || "").toLowerCase().includes(q) ||
      (o.sub || "").toLowerCase().includes(q));
  }, [options, query]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  React.useEffect(() => { setActive(0); }, [query, open]);

  React.useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${active}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  function pick(opt) {
    onChange(opt ? opt.value : "");
    setOpen(false);
    setQuery("");
  }

  function onKeyDown(e) {
    if (disabled) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      else setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      if (open) { e.preventDefault(); if (filtered[active]) pick(filtered[active]); }
      else { e.preventDefault(); setOpen(true); }
    } else if (e.key === "Escape") {
      if (open) { e.preventDefault(); setOpen(false); setQuery(""); }
    } else if (e.key === "Tab") {
      setOpen(false);
      setQuery("");
    }
  }

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 6,
        border: `1px solid ${open ? accent : "#D0D7E5"}`,
        borderRadius: 6, background: disabled ? "#F5F5F5" : "#fff",
        padding: "0 8px 0 10px", height: 32,
        boxShadow: open ? `0 0 0 2px ${accent}22` : "none",
      }}>
        <Search24Regular style={{ fontSize: 14, color: open ? accent : "#999", flexShrink: 0 }} />
        <input
          ref={inputRef}
          type="text"
          disabled={disabled}
          value={open ? query : (selected?.label || "")}
          placeholder={selected ? selected.label : placeholder}
          onChange={(e) => { setQuery(e.target.value); if (!open) setOpen(true); }}
          onFocus={() => { setOpen(true); setQuery(""); }}
          onClick={() => { if (!open) { setOpen(true); setQuery(""); } }}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && filtered[active] ? `${listId}-opt-${active}` : undefined}
          style={{
            flex: 1, minWidth: 0, border: "none", outline: "none",
            background: "transparent", fontSize: 13, fontFamily: "inherit",
            color: disabled ? "#999" : INK, height: "100%",
          }}
        />
        {selected && !disabled && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); pick(null); }}
            title={clearLabel || "Clear"}
            aria-label={clearLabel || "Clear selection"}
            style={{
              border: "none", background: "none", cursor: "pointer",
              color: "#AAA", fontSize: 15, lineHeight: 1, padding: "0 2px", flexShrink: 0,
            }}
          >
            ×
          </button>
        )}
        <ChevronDown12Regular
          onClick={() => { if (!disabled) { setOpen((o) => !o); setQuery(""); } }}
          style={{ fontSize: 12, color: "#999", flexShrink: 0, cursor: disabled ? "default" : "pointer" }}
        />
      </div>

      {open && !disabled && (
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
            zIndex: 400, background: "#fff", border: "1px solid #E4E8EF",
            borderRadius: 8, boxShadow: "0 8px 28px rgba(0,0,0,0.14)",
            maxHeight: 300, overflowY: "auto", minWidth: 260,
          }}
        >
          {clearLabel && (
            <button
              type="button"
              onClick={() => pick(null)}
              style={{
                display: "block", width: "100%", textAlign: "left", cursor: "pointer",
                padding: "9px 12px", border: "none", borderBottom: "1px solid #EEF0F4",
                background: !value ? `${accent}10` : "#fff",
                fontSize: 12.5, color: !value ? accent : "#666",
                fontWeight: !value ? 700 : 400, fontFamily: "inherit",
              }}
            >
              {clearLabel}
            </button>
          )}

          {filtered.length === 0 ? (
            <div style={{ padding: "18px 14px", textAlign: "center", color: "#AAA", fontSize: 12.5 }}>
              {emptyText}
            </div>
          ) : filtered.map((o, i) => {
            const on = o.value === value;
            const hot = i === active;
            return (
              <button
                key={o.value}
                type="button"
                id={`${listId}-opt-${i}`}
                data-idx={i}
                role="option"
                aria-selected={on}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(o)}
                style={{
                  display: "block", width: "100%", textAlign: "left", cursor: "pointer",
                  padding: "8px 12px", border: "none", borderLeft: `3px solid ${on ? accent : "transparent"}`,
                  background: hot ? `${accent}0F` : "#fff",
                  fontFamily: "inherit",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
                  <span style={{ fontSize: 12.5, fontWeight: on ? 700 : 600, color: NAVY, lineHeight: 1.35 }}>
                    {o.label}
                  </span>
                  {o.meta && (
                    <span style={{ fontSize: 10.5, color: MUTED, whiteSpace: "nowrap", flexShrink: 0 }}>{o.meta}</span>
                  )}
                </div>
                {o.sub && (
                  <div style={{ fontSize: 10.5, color: MUTED, marginTop: 1 }}>{o.sub}</div>
                )}
              </button>
            );
          })}

          {query.trim() && filtered.length > 0 && filtered.length < options.length && (
            <div style={{
              padding: "7px 12px", borderTop: "1px solid #EEF0F4", background: "#FAFBFC",
              fontSize: 10.5, color: MUTED, position: "sticky", bottom: 0,
            }}>
              {filtered.length} of {options.length} match “{query.trim()}”
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Date range control ────────────────────────────────────────────────────────

export function buildPresets(years) {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const minus = (months) => {
    const d = new Date(today);
    d.setMonth(d.getMonth() - months);
    return iso(d);
  };
  const presets = [
    { id: "all", label: "All time", from: "", to: "" },
    { id: "12m", label: "Last 12 months", from: minus(12), to: iso(today) },
    { id: "24m", label: "Last 24 months", from: minus(24), to: iso(today) },
  ];
  years.slice(0, 4).forEach((y) => {
    presets.push({ id: `y${y}`, label: y, from: `${y}-01-01`, to: `${y}-12-31` });
  });
  presets.push({ id: "custom", label: "Custom range", from: "", to: "" });
  return presets;
}

export function DateRangeControl({ presets, value, onChange, bounds, resultCount, totalCount }) {
  const active = presets.find((p) => p.id === value.preset) || presets[0];
  const isCustom = value.preset === "custom";

  return (
    <div style={{
      background: "#fff", border: "1px solid #E4E8EF", borderRadius: 12,
      padding: "12px 16px", marginBottom: 16,
      display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
    }}>
      <span style={{ fontSize: 10.5, fontWeight: 700, color: GOLD, letterSpacing: "0.6px", whiteSpace: "nowrap" }}>
        PERIOD
      </span>

      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {presets.map((p) => {
          const on = p.id === value.preset;
          return (
            <button
              key={p.id}
              onClick={() => onChange(
                p.id === "custom"
                  ? { preset: "custom", from: value.from || bounds.min, to: value.to || bounds.max }
                  : { preset: p.id, from: p.from, to: p.to },
              )}
              style={{
                padding: "5px 12px", borderRadius: 16, fontSize: 12,
                border: `1px solid ${on ? GOLD : "#E4E8EF"}`,
                background: on ? GOLD : "#fff",
                color: on ? "#fff" : "#555",
                fontWeight: on ? 700 : 400,
                cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
              }}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {isCustom && (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="date" value={value.from || ""} min={bounds.min} max={bounds.max}
            onChange={(e) => onChange({ ...value, preset: "custom", from: e.target.value })}
            style={dateInput}
            aria-label="From date"
          />
          <span style={{ color: MUTED, fontSize: 12 }}>to</span>
          <input
            type="date" value={value.to || ""} min={bounds.min} max={bounds.max}
            onChange={(e) => onChange({ ...value, preset: "custom", to: e.target.value })}
            style={dateInput}
            aria-label="To date"
          />
        </div>
      )}

      <span style={{ fontSize: 11.5, color: MUTED, marginLeft: "auto", whiteSpace: "nowrap" }}>
        {resultCount === totalCount
          ? `${totalCount} award${totalCount === 1 ? "" : "s"}`
          : `${resultCount} of ${totalCount} awards in range`}
        {active.id !== "all" && value.from && value.to && (
          <span style={{ marginLeft: 8, color: "#AAA" }}>
            {fmtShort(value.from)} – {fmtShort(value.to)}
          </span>
        )}
      </span>
    </div>
  );
}

const dateInput = {
  padding: "5px 9px", border: "1px solid #D0D7E5", borderRadius: 7,
  fontSize: 12.5, outline: "none", fontFamily: "inherit", color: INK,
};

function fmtShort(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" });
}
